import {colorForValueType} from "#material/helpers/color"
import {defaultUiPosition} from "#material/nodes/base/material/constants"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import type {MaterialSocketUiDefinition, MaterialUiInfo} from "#material/nodes/base/material/types/ui"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {
    MaterialNodeConstructorParameters,
    MaterialNodeFlattenedReturns,
    MaterialNodeInputs,
    MaterialNodeParameters,
    MaterialNodeReturns,
} from "#material/sockets/parameters"
import type {ThreeReturns} from "#material/sockets/three"
import type {MaterialContext} from "#material/types/context"
import type {Evaluator} from "@cm/graph-v3"
import {EditorNode} from "@cm/graph-v3"
import {truncateAt} from "@cm/utils"
import {v4 as uuid} from "uuid"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {MaterialNodeLike} from "#material/types/material-connection"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import type {SettingValueOfType} from "#material/sockets/definition"
import type {SettingValueType} from "#material/sockets/setting-value-type"
import type {DataResolver} from "#material/validation/data-resolver/index"

export abstract class MaterialNode<in out Sockets extends MaterialNodeSockets = any>
    extends EditorNode<MaterialNodeReturns<Sockets>, MaterialContext, MaterialNodeParameters<Sockets>, MaterialNodeConstructorParameters<Sockets>>
    implements MaterialNodeLike<Sockets>
{
    public abstract override get definition(): MaterialNodeDefinition<Sockets>

    public legacyId?: number

    override toJSON(): object {
        return {
            [this.nodeClass]: {
                id: this.id,
                legacyId: this.legacyId,
                ...this.parameters,
                inputs: Object.fromEntries(Object.entries(this.parameters.inputs).map(([key, value]) => [key, value?.toJSON()])),
            },
        }
    }

    override toString() {
        return `${this.nodeClass}<${this.legacyId} | ${this.instanceId}>: ${truncateAt(JSON.stringify(this.parameters))}`
    }

    override run = async (evaluator: Evaluator<MaterialContext>): Promise<MaterialNodeReturns<Sockets>> => {
        switch (evaluator.context.type) {
            case "cycles":
                return this.toCycles(evaluator as CyclesEvaluator)
            case "three":
                return this.toThree(evaluator as ThreeEvaluator)
        }
    }

    toCycles = (_evaluator: CyclesEvaluator): Promise<CyclesReturns<Sockets>> => {
        throw new Error(`Method toCycles not implemented for ${this.nodeClass}`)
    }

    toThree = (_evaluator: ThreeEvaluator): Promise<ThreeReturns<Sockets>> => {
        throw new Error(`Method toThree not implemented for ${this.nodeClass}`)
    }

    public get hierarchical() {
        return this.definition.hierarchical ?? false
    }

    flatten = async (resolve: DataResolver): Promise<MaterialNodeFlattenedReturns<Sockets>> => {
        if (!this.hierarchical) throw new Error(`Cannot flatten non-hierarchical node ${this.nodeClass}`)
        throw new Error(`Method flatten not implemented for ${this.nodeClass}`)
    }

    updateInputs(newInputs: MaterialNodeParameters<Sockets>["inputs"]) {
        this.updateParameters({inputs: newInputs})
    }

    updateInput<Key extends keyof MaterialNodeParameters<Sockets>["inputs"]>(key: Key, value: MaterialNodeParameters<Sockets>["inputs"][Key]) {
        this.updateInputs({
            ...this.parameters.inputs,
            [key]: value,
        })
    }

    unsetInput<Key extends keyof MaterialNodeParameters<Sockets>["inputs"]>(key: Key) {
        this.updateInputs(Object.fromEntries(Object.entries(this.parameters.inputs).filter(([k]) => k !== key)) as MaterialNodeInputs<Sockets>)
    }

    public uiInfo = (): MaterialUiInfo<MaterialNodeSockets> => {
        const uiDefinition = this.definition.ui
        const uiItems =
            uiDefinition?.items.map((uiItem) => {
                if (uiItem.custom) {
                    return {
                        definition: uiItem,
                        inputValue: undefined,
                        nodeId: this.id,
                        scalarValue: undefined,
                        uniqueId: `${this.id}-custom-${uuid()}`,
                    }
                }
                const inputValue = this.parameters.inputs[uiItem.name]
                const scalarValue = this.parameters.settings[uiItem.name]
                return {
                    definition: uiItem,
                    inputValue,
                    nodeId: this.id,
                    scalarValue,
                    uniqueId: `${this.id}-item-${uuid()}`,
                }
            }) ?? []
        return {
            color: uiDefinition?.color ?? "#000000",
            id: this.instanceId,
            label: uiDefinition?.label ?? this.constructor.name ?? "Undefined node",
            name: this.definition.exchangeFormat.nodeName,
            support: uiDefinition?.support ?? {
                cycles: false,
                three: false,
            },
            items: uiItems,
            editor: {
                width: uiDefinition?.width ? `${uiDefinition.width}px` : undefined,
                disableable: !!this.definition.ui?.disableable,
                disabled: !!this.parameters.ui?.disabled,
                position: this.parameters.ui?.position ?? defaultUiPosition,
            },
        } as MaterialUiInfo<MaterialNodeSockets>
    }

    public colorForSocket = (socketName: string, direction: "input" | "output") => {
        if (direction === "input") {
            const input = this.definition.ui?.items.find((item) => !item.custom && item.name === socketName) as MaterialSocketUiDefinition<Sockets> | undefined
            return colorForValueType(input?.valueType)
        } else {
            const output = this.definition.ui?.items.find((item) => !item.custom && item.name === socketName) as MaterialSocketUiDefinition<Sockets> | undefined
            return colorForValueType(output?.valueType)
        }
    }

    public get disableable() {
        return !!this.definition.ui?.disableable
    }
    public get disabled() {
        return !!this.parameters.ui?.disabled
    }
    public set disabled(newValue: boolean) {
        this.updateParameters({
            ui: {
                ...this.parameters.ui,
                disabled: newValue,
            },
        })
    }

    public get position() {
        return this.parameters.ui?.position ?? defaultUiPosition
    }
    public set position(newValue: {x: number; y: number}) {
        this.updateParameters({
            ui: {
                ...this.parameters.ui,
                position: newValue,
            },
        })
    }

    public getParameter(name: string) {
        return this.parameters.inputs[name] ?? this.parameters.settings[name]
    }

    public updateSetting = <Key extends keyof MaterialNodeParameters<Sockets>["settings"]>(
        key: Key,
        value: MaterialNodeParameters<Sockets>["settings"][Key],
    ) => {
        this.updateParameters({
            settings: {
                ...this.parameters.settings,
                [key]: value,
            },
        })
    }

    public applyDefaults() {
        this.definition.ui?.items?.forEach((setting) => {
            if (!setting.custom) {
                if (setting.setting) {
                    if (setting.defaultValue !== undefined && this.parameters.settings[setting.name] === undefined) {
                        this.updateSetting(setting.name, setting.defaultValue as SettingValueOfType<SettingValueType>)
                    }
                }
            }
        })
    }

    override replaceParameters(newParameters: MaterialNodeConstructorParameters<Sockets>, disableValidation?: boolean): void {
        super.replaceParameters(newParameters, disableValidation as true)
        this.refresh()
    }

    public refresh() {
        this.parents.forEach((parent) => {
            // MaterialGraph nodes have a markDirty method
            // we don't refer to MaterialGraph directly to avoid circular dependencies
            if ("markDirty" in parent) {
                ;(parent.markDirty as () => {})()
            }
        })
    }
}
